import { Box, FormControl, Grid, TextField, Autocomplete, Typography, Skeleton, Alert, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { customFieldInputSx, inputs } from './styles/CommonStyles';
import { CommodityDeterminationType, CommodityRecord } from '../../../../ts/Common';
import { useTranslation } from 'react-i18next';

interface SubItem {
    subItem: string;
    text: string;
    class: number;
    min?: number;
    max?: number | null;
}
interface Commodity {
    id: number;
    shortName: string;
    description: string;
    determinedBy: string;
    subItems: SubItem[];
    grouping?: string;
    type: CommodityType;
    classification?: string;
    freightClassMin?: number | null; // keeping for backward compatibility
    freightClassMax?: number | null; // keeping for backward compatibility
    minClass?: number | null;
    maxClass?: number | null;
}

export enum CommodityType {
    GROUP = "group",
    RULE = "rule",
    NOTE = "note"
}

interface CommoditiesProps {
    commodities: CommodityRecord[];
    commoditiesLoaded: boolean;
    onFreightClassSelected: (selectedCommodity: Commodity | null, selectedSubItem: SubItem | null) => void;
}

export default function Commodities({ commodities, commoditiesLoaded, onFreightClassSelected }: CommoditiesProps) {    
    const [dropdownValues, setDropdownValues] = useState<Commodity[]>([]);
    const [dropdownValuesLoaded, setDropdownValuesLoaded] = useState(false);
    const [selectedCommodity, setSelectedCommodity] = useState<Commodity | null>(null);
    const [selectedSubItem, setSelectedSubItem] = useState<SubItem | null>(null);
    const { t } = useTranslation();
    const theme = useTheme();

    const styles = {
        ...inputs,
        dropDown: {
            backgroundColor: 'var(--spectre-surface)',
            textArea: {
                color: '#4B4E65'
            },
            mt: 1
        },
        autocomplete: {
            ...customFieldInputSx,
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
                color: 'var(--spectre-custom-field-label)',
            },
        },
        title: {
            fontWeight: 'bold',
            mb: 1
        }
    }

    useEffect(() => {
        const fetchDropdownValues = async () => {
            if (commoditiesLoaded && commodities.length > 0) {
                try {
                    // Build query string and call Azure Function
                    const ids = commodities.map(c => c.nmfcId).join(',');
                    const url = `https://freight-classifier.azurewebsites.net/api/batchLookup?ids=${ids}`;
                    const response = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    });
                    const data: Commodity[] = await response.json();
                    
                    // Merge freight class min/max from commodities into the response data
                    const enrichedData = data.map(item => {
                        // Find matching commodity by nmfcId
                        const matchingCommodity = commodities.find(c => c.nmfcId === item.id.toString());
                        if (matchingCommodity) {
                            return {
                                ...item,
                                freightClassMin: matchingCommodity.minClass || null, // for backward compatibility
                                freightClassMax: matchingCommodity.maxClass || null, // for backward compatibility
                                minClass: matchingCommodity.minClass || null,
                                maxClass: matchingCommodity.maxClass || null
                            };
                        }
                        return item;
                    }); 
                    
                    // Filter out items where Type is 'group' or 'note'
                    const filtered = enrichedData.filter(item => item.type !== CommodityType.GROUP && item.type !== CommodityType.NOTE);
                    setDropdownValues(filtered);
                    setDropdownValuesLoaded(true);
                } catch (error) {
                    console.error('Error calling Azure Function:', error);
                }
            }
        };

        fetchDropdownValues();
    }, [commoditiesLoaded, commodities]);

    const handleCommodityChange = (newValue: Commodity | null) => {
        // Don't set if the commodity is not found (undefined)
        if (!newValue) {
            console.warn("Attempted to select undefined commodity");
            return;
        }
        
        setSelectedCommodity(newValue);
        setSelectedSubItem(null);
        
        // Notify parent component of selection change
        // If the commodity has a classification but no subitems, we don't need a subitem selection
        onFreightClassSelected(newValue, null);
    };

    // When subItem selection changes
    const handleSubItemChange = (subItem: SubItem) => {
        setSelectedSubItem(subItem);
        
        // Notify parent component of selection change
        onFreightClassSelected(selectedCommodity, subItem);
    };

    // Format the subItem display text
    const formatSubItemText = (subItem: SubItem) => {
        return `${subItem.text} (Class: ${subItem.class})`;
    };

    let loadingSkeleton = (
        <Box sx={styles.customFieldsBox} style={{ paddingTop: 16 }}>
            <Typography sx={styles.title}>{t('control.inputs.freightClassifier.title')}</Typography>
            <Grid
                container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Grid item md={11} xs={11} sx={styles.dropDown}>
                    <Skeleton variant="rectangular" height={40} />
                </Grid>
            </Grid>
        </Box>
    );

    let failedToLoad = (
        <Box sx={styles.customFieldsBox} style={{ paddingTop: 16 }}>
            <Typography sx={styles.title}>{t('control.inputs.freightClassifier.title')}</Typography>
            <Grid
                container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Alert sx={{ mt: 2 }} severity="error">{t('control.inputs.freightClassifier.failedToLoad')}</Alert>
            </Grid>
        </Box>
    );

    if (commoditiesLoaded === null) {
        return failedToLoad
    }

    // If not loaded or no commodities, return nothing
    if (commoditiesLoaded === false || !commodities.length) {
        return null;
    }

    // Commodities loaded but dropdown values not yet loaded
    if (commoditiesLoaded && !dropdownValuesLoaded) {
        return loadingSkeleton;
    }
    // If loaded and has data, show the component
    return (
        <Box sx={styles.customFieldsBox} style={{ paddingTop: 16 }}>
            <Typography sx={styles.title}>{t('control.inputs.freightClassifier.title')}</Typography>
            <Grid
                container
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
            >
                <Grid item md={11} xs={11} sx={styles.dropDown}>
                    <FormControl fullWidth>                        
                        <Autocomplete
                            options={dropdownValues}
                            getOptionLabel={(option) => {
                                if (!option) return "Error: Commodity not found";
                                return `${option.shortName || option.description} (${option.id})`;
                            }}
                            value={selectedCommodity}
                            onChange={(_, newValue) => handleCommodityChange(newValue)}
                            isOptionEqualToValue={(option, value) => option && value && option.id === value.id}                            
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label={t('control.inputs.freightClassifier.commodity')}
                                    error={selectedCommodity === undefined}
                                    helperText={selectedCommodity === undefined ? t('control.inputs.freightClassifier.error') : ""}
                                    sx={styles.autocomplete}
                                />
                            )}
                        />
                    </FormControl>
                </Grid>              
                {selectedCommodity && selectedCommodity.determinedBy === CommodityDeterminationType.MATERIAL && selectedCommodity.subItems && selectedCommodity.subItems.length > 0 && (
                    <Grid item md={11} xs={11} sx={styles.dropDown}>
                        <FormControl fullWidth>
                            <Autocomplete
                                options={selectedCommodity.subItems}
                                getOptionLabel={(option) => option ? formatSubItemText(option) : ""}
                                value={selectedSubItem}
                                onChange={(_, newValue) => {
                                    if (newValue) handleSubItemChange(newValue);
                                }}
                                isOptionEqualToValue={(option, value) => option && value && option.subItem === value.subItem}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        label={t('control.inputs.freightClassifier.material')}
                                            sx={styles.autocomplete}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid>
                )}                
                {selectedCommodity && selectedCommodity.determinedBy === CommodityDeterminationType.DENSITY && selectedCommodity.subItems && selectedCommodity.subItems.length > 0 && (
                    <Grid item md={11} xs={11} sx={{ mt: 1}}>
                        <Typography variant="body2" color="textSecondary">
                            {t('control.inputs.freightClassifier.densityMessage')}
                        </Typography>
                    </Grid>
                )}
                {selectedCommodity && (!selectedCommodity.subItems || selectedCommodity.subItems.length === 0) && selectedCommodity.classification && (
                    <Grid item md={11} xs={11} sx={{ mt: 1}}>
                        <Typography variant="body2" color="textSecondary">
                            {t('control.inputs.freightClassifier.classificationMessage', { classification: selectedCommodity.classification })}
                        </Typography>
                    </Grid>
                )}
                
                {selectedCommodity === undefined && (
                    <Grid item md={11} xs={11} sx={{ mt: 1}}>
                        <Typography variant="body2" color="error">
                            {t('control.inputs.freightClassifier.error')}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
};